import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Building2, Users, ClipboardList, ArrowUpRight, Plus } from "lucide-react";
import { toast } from "sonner";

const VERDICT_COLORS = {
    Retain: "text-emerald-300 border-emerald-500/40",
    Retrain: "text-amber-300 border-amber-500/40",
    Redeploy: "text-red-300 border-red-500/40",
};

export default function PartnerDashboard() {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [entering, setEntering] = useState(null);

    useEffect(() => {
        api.get("/v2/partner/dashboard").then(r => setData(r.data)).catch(() => {});
        api.get("/v2/partner/workspaces").then(r => setWorkspaces(r.data.workspaces || [])).catch(() => {});
    }, [api]);

    const enterWorkspace = async (ws) => {
        setEntering(ws.id);
        try {
            const r = await api.post(`/v2/partner/workspaces/${ws.id}/enter`);
            localStorage.setItem("partner_origin_token", localStorage.getItem("token"));
            localStorage.setItem("partner_acting_workspace_name", ws.name);
            localStorage.setItem("token", r.data.token);
            toast.success(`Entered ${ws.name}`);
            window.location.href = "/enterprise";
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not enter workspace");
            setEntering(null);
        }
    };

    if (!data) return <div className="text-gray-500 text-sm">Loading…</div>;

    const o = data.overview;
    const totalVerdicts = (o.verdict_distribution.Retain + o.verdict_distribution.Retrain + o.verdict_distribution.Redeploy) || 1;

    return (
        <div data-testid="partner-dashboard">
            <div className="mb-8 flex items-baseline justify-between">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">{data.partner.organization_name}</h1>
                    <p className="text-sm text-gray-500 mt-1 font-mono uppercase tracking-wider">
                        Partner · {data.partner.billing_tier} · {data.partner.billing_model.replace(/_/g, " ")}
                    </p>
                </div>
                <Link to="/partner/companies/new"
                    className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-gray-200 inline-flex items-center gap-2"
                    data-testid="provision-company-btn">
                    <Plus className="w-4 h-4" /> Provision New Company
                </Link>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-8" data-testid="portfolio-overview">
                <Kpi label="Companies" value={o.total_companies} icon={Building2} />
                <Kpi label="Candidates assessed" value={o.total_candidates} icon={Users} />
                <Kpi label="Active assessments" value={o.total_assessments} icon={ClipboardList} />
                <div className="bg-card border border-border/40 p-5">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-body">Verdict mix</p>
                    <div className="mt-3 space-y-1.5">
                        {Object.entries(o.verdict_distribution).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                                <span className={`font-mono ${VERDICT_COLORS[k]?.split(" ")[0] || ""}`}>{k}</span>
                                <span className="text-gray-400 font-mono">{v} · {((v/totalVerdicts)*100).toFixed(0)}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <h2 className="text-base font-display font-semibold text-white mb-3">Portfolio Companies</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="companies-grid">
                {workspaces.map(w => (
                    <button key={w.id} onClick={() => enterWorkspace(w)}
                        disabled={entering === w.id}
                        className="text-left bg-card border border-border/40 p-5 hover:border-white/40 transition-colors group disabled:opacity-60"
                        data-testid={`company-card-${w.id}`}>
                        <div className="flex items-start justify-between mb-2">
                            <h3 className="text-sm font-display font-semibold text-white group-hover:text-white">{w.name}</h3>
                            <ArrowUpRight className="w-4 h-4 text-gray-600 group-hover:text-white" />
                        </div>
                        <p className="text-xs text-gray-500 font-mono">{w.primary_industry || "—"} · {w.geographic_footprint || "—"}</p>
                        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                            <div>
                                <p className="text-gray-600 uppercase tracking-wider text-[10px]">Candidates</p>
                                <p className="text-white font-mono mt-0.5">{w.candidate_count}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 uppercase tracking-wider text-[10px]">Users</p>
                                <p className="text-white font-mono mt-0.5">{w.user_count}</p>
                            </div>
                        </div>
                        <p className="text-[10px] text-gray-600 mt-3 font-mono uppercase tracking-wider">
                            {entering === w.id ? "Entering…" : "Click to enter →"}
                        </p>
                    </button>
                ))}
                {workspaces.length === 0 && (
                    <div className="col-span-full bg-card border border-dashed border-border/60 p-10 text-center">
                        <p className="text-sm text-gray-500">No companies yet. Click "Provision New Company" to add your first portfolio company.</p>
                    </div>
                )}
            </div>

            <h2 className="text-base font-display font-semibold text-white mb-3">Recent Activity</h2>
            <div className="bg-card border border-border/40" data-testid="activity-feed">
                {data.recent_activity.length === 0 && (
                    <div className="px-5 py-8 text-xs text-gray-500 text-center">No activity yet across the portfolio.</div>
                )}
                {data.recent_activity.map((a, i) => (
                    <div key={i} className="px-5 py-3 border-b border-border/20 last:border-0 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white font-mono">{a.event_type.replace(/_/g, ' ')}</p>
                            <p className="text-[10px] text-gray-600 mt-0.5">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ""}</p>
                        </div>
                        <span className="text-[10px] text-gray-600 font-mono truncate max-w-[60%]">
                            {Object.entries(a.details || {}).slice(0, 2).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 24) : v}`).join(" · ")}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Kpi({ label, value, icon: Icon }) {
    return (
        <div className="bg-card border border-border/40 p-5">
            <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-body">{label}</p>
                <Icon className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-3xl font-display font-bold text-white font-mono">{value}</p>
        </div>
    );
}

